import { Fragment, ReactNode } from 'react';
import { splitLinks } from '../helpers/chatLinks';
import { MarkdownSpan, parseMarkdown } from '../helpers/chatMarkdown';
import { BodyLink } from './MessageBubble.styled';
import {
  MarkdownCode,
  MarkdownEm,
  MarkdownItem,
  MarkdownList,
  MarkdownParagraph,
  MarkdownPre,
  MarkdownRoot,
  MarkdownRow,
  MarkdownStrong,
  MarkdownTable,
  MarkdownTableWrap,
  MarkdownWord,
} from './MessageMarkdown.styled';

type Props = {
  body: string;
  /** True when the bubble is the coloured one - see MessageMarkdown.styled. */
  own: boolean;
  /**
   * The floated timestamp, rendered INSIDE the final block rather than after
   * them all. A float placed after a block-level element drops below it, which
   * would leave the time alone on its own line under every formatted answer;
   * tucked into the last paragraph it sits at the end of the last sentence,
   * exactly as it does in a plain bubble. A table or a code block cannot hold
   * it, so after one of those it gets a line of its own.
   */
  trailing?: ReactNode;
  /**
   * The answer is still arriving, so each block fades in as it lands. Off on
   * a finished message: see `blockEntrance`.
   */
  animate?: boolean;
};

/**
 * Viktor's answer, with its Markdown rendered.
 *
 * Only Viktor's rows come through here - see `chatMarkdown` for why a person's
 * text stays literal, and for what is and is not recognised. Bare URLs are
 * still found the way they are everywhere else in the chat, so a link Viktor
 * wrote as plain text is as tappable as one he wrapped in markdown.
 */
export const MessageMarkdown = ({ body, own, trailing, animate }: Props) => {
  const blocks = parseMarkdown(body);
  const lastBlock = blocks[blocks.length - 1];
  const trailingNeedsRow =
    !!trailing && (!lastBlock || lastBlock.kind === 'code' || lastBlock.kind === 'table');

  return (
    <MarkdownRoot>
      {blocks.map((block, index) => {
        // Keyed by position: this is a pure derivation of one string, so a
        // block has no identity beyond where it sits in it.
        const last = index === blocks.length - 1 && !trailingNeedsRow;

        switch (block.kind) {
          case 'list':
            return (
              <MarkdownList
                key={index}
                as={block.ordered ? 'ol' : 'ul'}
                $ordered={block.ordered}
              >
                {block.items.map((spans, itemIndex) => (
                  <MarkdownItem key={itemIndex}>
                    <Spans spans={spans} own={own} animate={animate} />
                    {last && itemIndex === block.items.length - 1 && trailing}
                  </MarkdownItem>
                ))}
              </MarkdownList>
            );
          case 'code':
            return (
              <MarkdownPre key={index} $own={own} $animate={animate}>
                {block.text}
              </MarkdownPre>
            );
          case 'table':
            return (
              <MarkdownTableWrap key={index}>
                <MarkdownTable $own={own}>
                  <thead>
                    {/* The row animates as one - a table arrives a row at a
                        time, and a header creeping in cell by cell would read
                        as the table still being built. */}
                    <MarkdownRow $animate={animate}>
                      {block.header.map((spans, cellIndex) => (
                        <th key={cellIndex}>
                          <Spans spans={spans} own={own} />
                        </th>
                      ))}
                    </MarkdownRow>
                  </thead>
                  <tbody>
                    {block.rows.map((row, rowIndex) => (
                      <MarkdownRow key={rowIndex} $animate={animate}>
                        {/* Padded to the header's width: a short row is a
                            row, not a shorter table. */}
                        {block.header.map((_, cellIndex) => (
                          <td key={cellIndex}>
                            {row[cellIndex] && (
                              <Spans spans={row[cellIndex]} own={own} />
                            )}
                          </td>
                        ))}
                      </MarkdownRow>
                    ))}
                  </tbody>
                </MarkdownTable>
              </MarkdownTableWrap>
            );
          default:
            return (
              <MarkdownParagraph key={index}>
                <Spans spans={block.spans} own={own} animate={animate} />
                {last && trailing}
              </MarkdownParagraph>
            );
        }
      })}

      {trailingNeedsRow && <MarkdownParagraph>{trailing}</MarkdownParagraph>}
    </MarkdownRoot>
  );
};

/**
 * The text, one element per word while the answer is arriving.
 *
 * This is the whole reason the entrance is not on the paragraph: a block is
 * created as soon as the first word or two of a sentence lands, so animating
 * the block fades in a fragment and lets the rest of the sentence type itself
 * in with nothing at all. Words are what the stream actually delivers, so
 * words are what arrive.
 *
 * Keyed by position, which is what keeps it cheap and still: every delta
 * re-parses the whole body, but a word that was already there keeps its key,
 * so React leaves the element alone and the animation does not replay. Only
 * the words at the end are new.
 *
 * Whitespace is kept as its own token rather than trimmed, because the
 * paragraph is `pre-wrap` - the newlines between Viktor's lines are his, and
 * splitting them away would run his sentences together.
 *
 * No wrappers at all once the answer is stored: a finished message is plain
 * text again.
 */
const Words = ({ text, animate }: { text: string; animate?: boolean }) => {
  if (!animate) return <>{text}</>;
  return (
    <>
      {text.split(/(\s+)/).map((token, index) =>
        token.trim() ? (
          <MarkdownWord key={index} $animate>
            {token}
          </MarkdownWord>
        ) : (
          <Fragment key={index}>{token}</Fragment>
        )
      )}
    </>
  );
};

const Spans = ({
  spans,
  own,
  animate,
}: {
  spans: MarkdownSpan[];
  own: boolean;
  /** Fade each word in as it arrives - see `Words`. */
  animate?: boolean;
}) => (
  <>
    {spans.map((span, index) => {
      switch (span.kind) {
        case 'bold':
          return (
            <MarkdownStrong key={index}>
              <Words text={span.text} animate={animate} />
            </MarkdownStrong>
          );
        case 'italic':
          return (
            <MarkdownEm key={index}>
              <Words text={span.text} animate={animate} />
            </MarkdownEm>
          );
        case 'code':
          return (
            <MarkdownCode key={index} $own={own}>
              <Words text={span.text} animate={animate} />
            </MarkdownCode>
          );
        case 'link':
          return (
            <BodyLink
              key={index}
              href={span.href}
              $own={own}
              /* A new tab with the opener severed, like every other outbound
                 link in the chat - the destination is whatever Viktor wrote. */
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              <Words text={span.text} animate={animate} />
            </BodyLink>
          );
        default:
          return (
            <Fragment key={index}>
              {splitLinks(span.text).map((piece, pieceIndex) =>
                piece.href ? (
                  <BodyLink
                    key={pieceIndex}
                    href={piece.href}
                    $own={own}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Words text={piece.text} animate={animate} />
                  </BodyLink>
                ) : (
                  <Fragment key={pieceIndex}>
                    <Words text={piece.text} animate={animate} />
                  </Fragment>
                )
              )}
            </Fragment>
          );
      }
    })}
  </>
);
